<template>
  <button
    v-if="shouldShowProgressButton"
    type="button"
    :class="['ank--progress-widget', { 'ank--progress-widget--inline': inline }]"
    @click="openProgressModal"
  >
    <div class="ank--progress-top">
      <div class="ank--progress-ring" aria-hidden="true" :style="progressRingStyle"></div>
      <div class="ank--progress-text">
        <div class="ank--progress-value">{{ progressPercent }}%</div>
        <div>{{ $t('agenNaikKelasProgress.title1') }}</div>
        <div>{{ $t('agenNaikKelasProgress.title2') }}</div>
      </div>
    </div>
  </button>
</template>

<script>
import { mapState } from 'vuex';

export default {
  name: 'agen-naik-kelas-progress',
  props: {
    inline: {
      type: Boolean,
      default: false,
    },
    showWhenComplete: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      showProgressButton: false,
      pollingTimer: null,
      pollingInProgress: false,
    };
  },
  computed: {
    ...mapState({
      loggedIn: (state) => state.global.loggedIn,
      percentageSuccess: (state) => state.agenNaikKelas.percentageSuccess,
    }),
    rawProgressPercent() {
      const value = Number(this.percentageSuccess || 0);
      if (value < 0) return 0;
      if (value > 100) return 100;
      return value;
    },
    progressPercent() {
      return Math.round(this.rawProgressPercent);
    },
    isProgressComplete() {
      return this.rawProgressPercent >= 100;
    },
    shouldShowProgressButton() {
      return this.showProgressButton && (this.showWhenComplete || !this.isProgressComplete);
    },
    progressRingStyle() {
      const degree = Math.round((this.progressPercent / 100) * 360);
      return {
        background: `conic-gradient(#25b052 0deg, #25b052 ${degree}deg, #fff ${degree}deg, #fff 360deg)`,
      };
    },
  },
  async mounted() {
    if (!this.loggedIn) return;
    await this.refreshProgress();
    this.startPolling();
  },
  beforeDestroy() {
    this.stopPolling();
  },
  watch: {
    '$route.fullPath'() {
      if (!this.loggedIn) return;
      this.refreshProgress();
    },
    loggedIn(value) {
      if (value) {
        this.refreshProgress();
        this.startPolling();
        return;
      }

      this.stopPolling();
      this.showProgressButton = false;
    },
  },
  methods: {
    async refreshProgress() {
      if (!this.loggedIn) {
        this.showProgressButton = false;
        return;
      }
      if (this.pollingInProgress) return;
      this.pollingInProgress = true;
      try {
        const payload = await this.$store.dispatch('agenNaikKelas/getTimelineProgress');
        this.showProgressButton = !!(
          payload &&
          typeof payload === 'object' &&
          Object.keys(payload).length > 0
        );
      } catch (e) {
        this.showProgressButton = false;
      } finally {
        this.pollingInProgress = false;
      }
    },
    startPolling() {
      this.stopPolling();
      this.pollingTimer = setInterval(() => {
        this.refreshProgress();
      }, 3 * 60 * 1000);
    },
    stopPolling() {
      if (this.pollingTimer) {
        clearInterval(this.pollingTimer);
        this.pollingTimer = null;
      }
    },
    openProgressModal() {
      this.$modal.show('agen-naik-kelas-progress-modal');
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../assets/scss/utils/variables';

.ank--progress-widget {
  position: fixed;
  right: 96px;
  bottom: 18px;
  z-index: 99;
  padding: 12px 24px;
  border-radius: 999px;
  background: linear-gradient(145deg, #ca121a 0%, #9e0f15 100%);
  box-shadow: 0 8px 18px rgba(121, 12, 17, 0.35);
  text-decoration: none;
  border: none;
  outline: none;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  display: flex;
  align-items: center;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 11px 22px rgba(121, 12, 17, 0.42);
  }

  @media #{$phones} {
    right: 16px;
    bottom: 76px;
    width: auto;
    max-width: calc(100vw - 32px);
    min-height: 52px;
    padding: 8px 14px 8px 10px;
    box-shadow: 0 8px 18px rgba(121, 12, 17, 0.28);
  }
}

.ank--progress-widget--inline {
  position: relative;
  right: auto;
  bottom: auto;
  margin-top: 16px;
  z-index: 1;
}

.ank--progress-top {
  display: flex;
  align-items: center;
  width: 100%;
}

.ank--progress-ring {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  margin-right: 18px;
  flex-shrink: 0;
  position: relative;
  margin-top: 1px;
  background: conic-gradient(#25b052 0deg, #25b052 54deg, #fff 54deg, #fff 360deg);

  &:before {
    content: '';
    position: absolute;
    inset: 6px;
    border-radius: 50%;
    background: linear-gradient(145deg, #ca121a 0%, #9e0f15 100%);
  }

  @media #{$phones} {
    width: 36px;
    height: 36px;
    margin-right: 10px;

    &:before {
      inset: 5px;
    }
  }
}

.ank--progress-text {
  font-family: Poppins-SemiBold;
  font-size: 38px;
  color: $color-white;
  letter-spacing: 0;
}

.ank--progress-value {
  font-family: Poppins-Bold;
  font-size: $font-xxl;
  text-align: left;
  line-height: 0.9;

  @media #{$phones} {
    font-size: 16px;
    line-height: 1;
  }
}

.ank--progress-text > div:not(.ank--progress-value) {
  font-size: $font-xs;
  text-align: left;
  line-height: 1.1;

  @media #{$phones} {
    max-width: 104px;
    font-size: 10px;
    line-height: 1.15;
  }
}
</style>
